// =============================================================================
// Setup2FA — obowiązkowa konfiguracja 2FA (TOTP). Trasa /setup-2fa.
// CELOWO poza TwoFactorGate (wymaga tylko logowania) — inaczej pętla redirect.
//
// Flow: cleanup porzuconych (niezweryfikowanych) czynników → enroll → QR + secret
// → challenge + verify po factorId → sesja podnosi się do aal2 → wejście do panelu.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ShieldCheck } from "lucide-react";
import { RecommendedAuthApp } from "@/components/RecommendedAuthApp";

/** Nazwa serwisu pokazywana w aplikacji uwierzytelniającej (obok adresu e-mail konta).
 *  Podawana JAWNIE — nie dziedziczy po Site URL projektu Supabase, więc zmiana
 *  Site URL nie podmieni nazwy w telefonach użytkowników. */
const TOTP_ISSUER = "dashboard.pbnsd666.website";

interface EnrollData {
  id: string;
  qr: string;
  secret: string;
}

/** Numerowany nagłówek kroku (1/2/3) — czytelny podział konfiguracji. */
function StepHeader({ n, title }: { n: number; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
        {n}
      </span>
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
  );
}

const Setup2FA = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [enroll, setEnroll] = useState<EnrollData | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [initError, setInitError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      try {
        // Już ma pełne 2FA → nie ma tu czego robić.
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aal?.currentLevel === "aal2") {
          navigate("/", { replace: true });
          return;
        }

        const { data: existing } = await supabase.auth.mfa.listFactors();
        const totp = existing?.totp ?? [];

        // Ma już ZWERYFIKOWANY czynnik → potrzebny jest kod przy logowaniu, nie enroll.
        if (totp.some((f) => f.status === "verified")) {
          navigate("/auth", { replace: true });
          return;
        }

        // Sprzątnij porzucone, niezweryfikowane czynniki z poprzednich prób.
        for (const f of totp) {
          if (f.status !== "verified") {
            await supabase.auth.mfa.unenroll({ factorId: f.id });
          }
        }

        // issuer = nazwa widoczna w aplikacji uwierzytelniającej. BEZ tego Supabase
        // bierze Site URL projektu (np. "localhost:5173") — brzydkie i mylące.
        const { data, error: enrollErr } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          issuer: TOTP_ISSUER,
          friendlyName: `TOTP ${Date.now()}`,
        });
        if (enrollErr || !data) {
          setInitError(enrollErr?.message || "Nie udało się rozpocząć konfiguracji 2FA.");
          return;
        }
        setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
      } catch (err) {
        setInitError(err instanceof Error ? err.message : "Nieoczekiwany błąd konfiguracji 2FA.");
      }
    })();
  }, [navigate]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enroll || verifying) return;
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Wpisz 6-cyfrowy kod z aplikacji.");
      return;
    }
    setVerifying(true);
    setError("");
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: enroll.id });
      if (chErr || !challenge) {
        setError("Nie udało się rozpocząć weryfikacji. Odśwież stronę i spróbuj ponownie.");
        return;
      }
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: enroll.id,
        challengeId: challenge.id,
        code: trimmed,
      });
      if (vErr) {
        setError("Nieprawidłowy kod. Spróbuj ponownie.");
        setCode("");
        return;
      }
      // Sesja jest już aal2 → bramka wpuści.
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd weryfikacji.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex flex-col items-center gap-2 mb-2">
            <ShieldCheck className="h-10 w-10 text-primary" />
            <CardTitle className="text-2xl font-bold">Skonfiguruj 2FA</CardTitle>
          </div>
          <CardDescription className="text-base">
            Uwierzytelnianie dwuskładnikowe jest wymagane. Skonfiguruj je raz — przy każdym kolejnym logowaniu podasz
            6-cyfrowy kod.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {initError ? (
            <>
              <Alert variant="destructive">
                <AlertDescription>{initError}</AlertDescription>
              </Alert>
              <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
                Spróbuj ponownie
              </Button>
            </>
          ) : !enroll ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Przygotowuję kod QR…
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              {/* KROK 1 — aplikacja */}
              <section className="space-y-2">
                <StepHeader n={1} title="Zainstaluj aplikację uwierzytelniającą" />
                <RecommendedAuthApp />
              </section>

              {/* KROK 2 — skanowanie */}
              <section className="space-y-2">
                <StepHeader n={2} title="Zeskanuj ten kod w aplikacji" />
                <p className="text-xs text-muted-foreground">
                  W aplikacji wybierz „Dodaj konto" / „+" i zeskanuj poniższy kod QR.
                </p>
                {/* qr_code z Supabase to gotowy SVG jako data URL — zwykły <img>, białe tło pod QR */}
                <div className="mx-auto w-fit rounded-lg bg-white p-3">
                  <img src={enroll.qr} alt="Kod QR do konfiguracji 2FA" className="h-44 w-44" />
                </div>
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Nie możesz zeskanować? Wpisz klucz ręcznie</summary>
                  <code className="mt-1 block break-all rounded bg-muted px-2 py-1.5 font-mono">{enroll.secret}</code>
                </details>
              </section>

              {/* KROK 3 — kod */}
              <section className="space-y-2">
                <StepHeader n={3} title="Wpisz 6-cyfrowy kod z aplikacji" />
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="mx-auto w-44 text-center font-mono text-2xl tracking-widest"
                  autoFocus
                />

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button type="submit" className="w-full" disabled={verifying || code.length !== 6}>
                  {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {verifying ? "Weryfikuję…" : "Aktywuj 2FA"}
                </Button>
              </section>
            </form>
          )}

          <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => signOut()}>
            Wyloguj się
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Setup2FA;
